/**
 * Discounts Tab Component
 *
 * Lets a super admin grant existing discounts to a specific account and revoke
 * them. Assignments drive the tenant's effective price (see getDiscountedPrice
 * on the API) and trigger a notification email to the account owner.
 */
import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Label,
  Input,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@respectly/ui';
import {
  Plus,
  Trash2,
  Ticket,
  User as UserIcon,
  Calendar,
  Percent,
  DollarSign,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { format } from 'date-fns';
import { useAccountsStore } from '../../../stores/useAccountsStore';
import { useDiscountsStore } from '../../../stores/useDiscountsStore';
import {
  DiscountStatusBadge,
  DiscountScopeBadge,
  DiscountCodeBadge,
  getDiscountStatus,
} from '../../discounts/DiscountStatusBadge';

interface DiscountsTabProps {
  accountId: string;
}

// Per-account limit overrides persist on TenantDiscount.limitOverrides and are
// merged (max-wins) over the plan defaults by getEffectivePlanLimits on the API.
// Keys must match Plan schema fields. Mirrors DiscountForm's field set.
const LIMIT_OVERRIDE_FIELDS: { key: string; label: string; group: string }[] = [
  { key: 'smsCreditsPerMonth', label: 'SMS Credits/Month', group: 'Messaging' },
  { key: 'emailCreditsPerMonth', label: 'Email Credits/Month', group: 'Messaging' },
  { key: 'aiCreditsPerMonth', label: 'AI Credits/Month', group: 'Messaging' },
  { key: 'maxUsers', label: 'Team Members (Users)', group: 'Team & Contacts' },
  { key: 'maxContacts', label: 'Max Contacts', group: 'Team & Contacts' },
  { key: 'maxForms', label: 'Max Forms', group: 'Team & Contacts' },
  { key: 'maxSmsPerBroadcast', label: 'Max SMS per Broadcast', group: 'Broadcasting' },
  { key: 'maxEmailPerBroadcast', label: 'Max Emails per Broadcast', group: 'Broadcasting' },
];

const LIMIT_GROUPS = ['Messaging', 'Team & Contacts', 'Broadcasting'];

function DiscountValue({ type, value }: { type: 'percentage' | 'fixed'; value: number }) {
  if (type === 'percentage') {
    return (
      <span className="flex items-center gap-1 font-semibold text-slate-800 dark:text-slate-200">
        <Percent className="h-3.5 w-3.5 text-violet-500" />
        {value}%
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 font-semibold text-slate-800 dark:text-slate-200">
      <DollarSign className="h-3.5 w-3.5 text-emerald-500" />
      {value.toFixed(2)}
    </span>
  );
}

export function DiscountsTab({ accountId }: DiscountsTabProps) {
  const {
    accountDiscounts,
    isLoadingDiscounts,
    isPerformingAction,
    loadAccountDiscounts,
    assignDiscount,
    removeAccountDiscount,
  } = useAccountsStore();

  const { discounts, isLoadingList, loadDiscounts } = useDiscountsStore();
  const { toast } = useToast();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDiscountId, setSelectedDiscountId] = useState('');
  const [notes, setNotes] = useState('');
  const [showLimitOverrides, setShowLimitOverrides] = useState(false);
  const [limitOverrides, setLimitOverrides] = useState<Record<string, string>>({});

  useEffect(() => {
    loadAccountDiscounts(accountId);
  }, [accountId, loadAccountDiscounts]);

  const openAssignDialog = () => {
    setSelectedDiscountId('');
    setNotes('');
    setLimitOverrides({});
    setShowLimitOverrides(false);
    setIsDialogOpen(true);
    loadDiscounts(1);
  };

  // Only offer discounts that are currently usable and not already assigned.
  const assignedIds = new Set(accountDiscounts.map((a) => a.discountId));
  const assignableDiscounts = discounts.filter(
    (d) => getDiscountStatus(d) === 'active' && !assignedIds.has(d.id)
  );

  const handleAssign = async () => {
    if (!selectedDiscountId) return;

    // Collect only the fields the admin actually filled with a valid number.
    // Everything else stays at the plan default (no override stored).
    const overrides: Record<string, number> = {};
    for (const [key, raw] of Object.entries(limitOverrides)) {
      if (raw.trim() === '') continue;
      const num = Number(raw);
      if (Number.isFinite(num)) overrides[key] = num;
    }

    const success = await assignDiscount(accountId, {
      discountId: selectedDiscountId,
      notes: notes.trim() || undefined,
      limitOverrides: Object.keys(overrides).length > 0 ? overrides : undefined,
    });

    if (success) {
      toast({
        title: 'Discount granted',
        description: 'The customer has been notified by email.',
        variant: 'success',
      });
      setIsDialogOpen(false);
      setSelectedDiscountId('');
      setNotes('');
      setLimitOverrides({});
      setShowLimitOverrides(false);
    } else {
      toast({
        title: 'Could not grant discount',
        description: useAccountsStore.getState().actionError?.message ?? 'Please try again.',
        variant: 'error',
      });
    }
  };

  const handleRemove = async (assignmentId: string, discountName: string) => {
    if (!window.confirm(`Remove "${discountName}" from this account?`)) return;

    const success = await removeAccountDiscount(accountId, assignmentId);
    toast(
      success
        ? { title: 'Discount removed', variant: 'success' }
        : {
            title: 'Could not remove discount',
            description: useAccountsStore.getState().actionError?.message ?? 'Please try again.',
            variant: 'error',
          }
    );
  };

  if (isLoadingDiscounts) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Account Discounts</CardTitle>
          <Skeleton className="h-9 w-32" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="rounded-lg border p-4">
                <Skeleton className="h-4 w-1/3 mb-2" />
                <Skeleton className="h-3 w-1/4" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Account Discounts ({accountDiscounts.length})</CardTitle>
        <Button size="sm" onClick={openAssignDialog}>
          <Plus className="mr-1.5 h-4 w-4" />
          Grant Discount
        </Button>
      </CardHeader>
      <CardContent>
        {accountDiscounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="rounded-2xl bg-slate-100 p-4 dark:bg-slate-800">
              <Ticket className="h-10 w-10 text-slate-400" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
              No discounts granted
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Grant an existing discount to apply it to this account&apos;s billing.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {accountDiscounts.map((assignment) => (
              <div
                key={assignment.id}
                className="rounded-lg border p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-900 dark:text-white">
                        {assignment.discount.name}
                      </span>
                      {assignment.discount.code && (
                        <DiscountCodeBadge code={assignment.discount.code} />
                      )}
                      <DiscountScopeBadge scope={assignment.discount.scope} />
                      <DiscountStatusBadge discount={assignment.discount} />
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      <DiscountValue
                        type={assignment.discount.type}
                        value={assignment.discount.value}
                      />
                      <span className="flex items-center gap-1">
                        <UserIcon className="h-3.5 w-3.5" />
                        {assignment.assignedBy}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {format(new Date(assignment.assignedAt), 'MMM d, yyyy')}
                      </span>
                    </div>

                    {assignment.limitOverrides &&
                      Object.keys(assignment.limitOverrides).length > 0 && (
                        <p className="text-xs text-slate-500">
                          <span className="font-medium">Limit overrides:</span>{' '}
                          {Object.entries(assignment.limitOverrides)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(', ')}
                        </p>
                      )}

                    {assignment.notes && (
                      <p className="text-sm text-slate-600 dark:text-slate-400">{assignment.notes}</p>
                    )}
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-red-500 hover:text-red-600"
                    onClick={() => handleRemove(assignment.id, assignment.discount.name)}
                    disabled={isPerformingAction}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Grant Discount Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5 text-indigo-500" />
              Grant Discount
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Discount</Label>
              <Select value={selectedDiscountId} onValueChange={setSelectedDiscountId}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={isLoadingList ? 'Loading discounts…' : 'Choose a discount'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {assignableDiscounts.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} — {d.type === 'percentage' ? `${d.value}%` : `$${d.value.toFixed(2)}`}
                      {d.code ? ` (${d.code})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isLoadingList && assignableDiscounts.length === 0 && (
                <p className="text-xs text-slate-500">
                  No active discounts available to grant. Create one in Plan Setup → Discounts first.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Why is this discount being granted?"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>

            {/* Per-account limit overrides (e.g. raise the team-member cap for
                this account only). Stored on TenantDiscount.limitOverrides. */}
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => setShowLimitOverrides((v) => !v)}
                className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
              >
                {showLimitOverrides ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                Limit overrides (optional)
              </button>
              <p className="ml-6 text-xs text-slate-400">
                Raise specific plan limits (e.g. Team Members) just for this account. Leave blank to
                keep plan defaults.
              </p>

              {showLimitOverrides && (
                <div className="mt-3 space-y-4">
                  {LIMIT_GROUPS.map((group) => {
                    const groupFields = LIMIT_OVERRIDE_FIELDS.filter((f) => f.group === group);
                    return (
                      <div key={group}>
                        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                          {group}
                        </p>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {groupFields.map((field) => (
                            <div key={field.key} className="space-y-1">
                              <Label className="text-xs">{field.label}</Label>
                              <Input
                                type="number"
                                min="0"
                                placeholder="Plan default"
                                value={limitOverrides[field.key] ?? ''}
                                onChange={(e) =>
                                  setLimitOverrides((prev) => ({
                                    ...prev,
                                    [field.key]: e.target.value,
                                  }))
                                }
                                className="h-8 text-sm"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <p className="text-xs text-slate-500">
              The account owner will receive an email letting them know about this discount.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssign} disabled={!selectedDiscountId || isPerformingAction}>
              {isPerformingAction && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Grant Discount
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
